import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Upload, Send, FileText, Image, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/lib/api';

interface ClientCampaignTriggerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ClientCampaignTrigger: React.FC<ClientCampaignTriggerProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasMedia, setHasMedia] = useState(false);
  const [sendImmediately, setSendImmediately] = useState(true);
  const [campaignData, setCampaignData] = useState({
    name: '',
    message: '',
    send_interval_seconds: 45,
    daily_limit: '',
    whatsapp_connection_id: '',
    scheduled_at: '',
    min_interval_seconds: 45,
    max_interval_seconds: 180
  });
  const [contactsFile, setContactsFile] = useState<File | null>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [whatsappConnections, setWhatsappConnections] = useState<any[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [syncingConnectChat, setSyncingConnectChat] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();

  // Carregar conexões WhatsApp quando o modal abrir
  useEffect(() => {
    if (open) {
      loadWhatsappConnections();
    }
  }, [open]);

  const loadWhatsappConnections = async () => {
    try {
      setLoadingConnections(true);
      console.log('🔍 [DEBUG] Iniciando carregamento de conexões WhatsApp...');
      console.log('🔍 [DEBUG] Usuário logado:', user);
      console.log('🔍 [DEBUG] API Base URL:', import.meta.env.VITE_API_BASE_URL);
      console.log('🔍 [DEBUG] URL completa:', `${import.meta.env.VITE_API_BASE_URL || '/api'}/whatsapp/client/current`);
      
      const response = await apiService.get('/whatsapp/client/current');
      
      console.log('🔍 [DEBUG] Response completa:', response);
      console.log('📡 [DEBUG] Response.connections:', response?.connections);
      
      const connections = response?.connections || [];
      console.log('✅ [DEBUG] Conexões processadas:', connections.length);
      
      setWhatsappConnections(connections);
      
      // Se houver apenas uma conexão, selecionar automaticamente
      if (connections.length === 1) {
        console.log('🔄 [DEBUG] Selecionando automaticamente conexão única:', connections[0].id);
        setCampaignData(prev => ({
          ...prev,
          whatsapp_connection_id: connections[0].id.toString()
        }));
      }
      
      if (connections.length === 0) {
        console.error('❌ [DEBUG] Nenhuma conexão foi retornada!');
        console.error('❌ [DEBUG] Verifique se o usuário está logado como cliente correto');
      }
      
    } catch (error: any) {
      console.error('❌ [DEBUG] Erro completo:', error);
      console.error('❌ [DEBUG] Error response:', error.response);
      console.error('❌ [DEBUG] Error response data:', error.response?.data);
      console.error('❌ [DEBUG] Error status:', error.response?.status);
      
      toast({
        title: "Erro ao carregar conexões",
        description: `Detalhes: ${error.response?.data?.error || error.message || "Erro desconhecido"}`,
        variant: "destructive"
      });
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleSyncConnectChat = async () => {
    if (!user?.client_id) {
      toast({
        title: "Erro",
        description: "Cliente não identificado",
        variant: "destructive"
      });
      return;
    }

    setSyncingConnectChat(true);
    try {
      // Primeiro, buscar conexões atualizadas do banco de dados
      console.log('🔄 Carregando conexões atuais do banco...');
      const currentData = await apiService.get(`/whatsapp/client/${user.client_id}`);
      let currentConnections = currentData.connections || [];
      console.log(`📋 ${currentConnections.length} conexões encontradas no banco`);

      // Sincronizar conexões usando o novo endpoint
      const response = await apiService.post('/connectchat/sync-own-connections');
      
      if (response.success) {
        const stats = response.stats;
        
        if (stats.synced > 0) {
          toast({
            title: "Sincronização concluída",
            description: `${stats.synced} conexões importadas com sucesso!`,
            variant: "default"
          });
        } else if (stats.total_found > 0 && stats.synced === 0) {
          toast({
            title: "Sincronização concluída",
            description: `Todas as ${stats.total_found} conexões do ConnectChat já estão sincronizadas!`,
            variant: "default"
          });
        } else {
          toast({
            title: "Nenhuma conexão encontrada",
            description: "Nenhuma conexão encontrada no ConnectChat",
            variant: "destructive"
          });
        }

        // Recarregar conexões
        await loadWhatsappConnections();

      } else {
        toast({
          title: "Erro na sincronização",
          description: response.message || "Erro desconhecido",
          variant: "destructive"
        });
      }

    } catch (error: any) {
      console.error('Erro ao sincronizar ConnectChat:', error);
      toast({
        title: "Erro na sincronização",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setSyncingConnectChat(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setContactsFile(file);
    }
  };

  const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMediaFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!campaignData.name.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, insira um nome para a campanha.",
        variant: "destructive"
      });
      return;
    }

    if (!campaignData.message.trim()) {
      toast({
        title: "Mensagem obrigatória", 
        description: "Por favor, insira a mensagem a ser enviada.",
        variant: "destructive"
      });
      return;
    }

    if (!campaignData.whatsapp_connection_id) {
      toast({
        title: "Conexão WhatsApp obrigatória",
        description: "Por favor, selecione uma conexão WhatsApp para enviar as mensagens.",
        variant: "destructive"
      });
      return;
    }

    if (!sendImmediately && !campaignData.scheduled_at) {
      toast({
        title: "Data de agendamento obrigatória",
        description: "Por favor, selecione uma data e hora para agendar a campanha.",
        variant: "destructive"
      });
      return;
    }

    if (!sendImmediately && campaignData.scheduled_at) {
      const scheduledDate = new Date(campaignData.scheduled_at);
      const now = new Date();
      if (scheduledDate <= now) {
        toast({
          title: "Data inválida",
          description: "A data de agendamento deve ser no futuro.",
          variant: "destructive"
        });
        return;
      }
    }

    if (!contactsFile) {
      toast({
        title: "Arquivo de contatos obrigatório",
        description: "Por favor, selecione um arquivo com os contatos.",
        variant: "destructive"
      });
      return;
    }

    if (hasMedia && !mediaFile) {
      toast({
        title: "Arquivo de mídia obrigatório",
        description: "Você habilitou mídia mas não selecionou nenhum arquivo.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // 1. Criar a campanha
      const campaignPayload = {
        ...campaignData,
        message_content: campaignData.message, // Mapear message para message_content
        campaign_type: 'avulsa',
        whatsapp_connection_id: parseInt(campaignData.whatsapp_connection_id) || 0,
        daily_limit: campaignData.daily_limit && campaignData.daily_limit.trim() !== ''
          ? parseInt(campaignData.daily_limit) || null
          : null,
        scheduled_at: sendImmediately ? null : new Date(campaignData.scheduled_at).toISOString(),
        min_interval_seconds: campaignData.min_interval_seconds,
        max_interval_seconds: campaignData.max_interval_seconds,
        // Determinar o endpoint baseado na presença de mídia
        integration_config: {
          endpoint: hasMedia ? '/sendurl' : '/sendtext'
        }
      };

      console.log('📤 Criando campanha:', campaignPayload);

      const campaignResponse = await apiService.post('/campaigns/create', campaignPayload);
      console.log('🔍 [DEBUG] Resposta completa da criação:', campaignResponse);
      console.log('🔍 [DEBUG] Tipo da resposta:', typeof campaignResponse);
      console.log('🔍 [DEBUG] Campaign object:', campaignResponse?.campaign);
      console.log('🔍 [DEBUG] ID da campanha:', campaignResponse?.campaign?.id);
      
      const campaignId = campaignResponse?.campaign?.id;
      
      if (!campaignId) {
        throw new Error(`Campanha criada mas ID não retornado. Resposta: ${JSON.stringify(campaignResponse)}`);
      }

      console.log('✅ Campanha criada com ID:', campaignId);

      // 2. Upload dos contatos
      const contactsFormData = new FormData();
      contactsFormData.append('file', contactsFile);

      console.log('📤 Fazendo upload dos contatos...');
      await apiService.post(`/campaigns/${campaignId}/upload-contacts`, contactsFormData);

      console.log('✅ Contatos enviados');

      // 3. Upload da mídia (se houver)
      if (hasMedia && mediaFile) {
        const mediaFormData = new FormData();
        mediaFormData.append('file', mediaFile);

        console.log('📤 Fazendo upload da mídia...');
        await apiService.post(`/campaigns/${campaignId}/upload-media`, mediaFormData);
        console.log('✅ Mídia enviada');
      }

      // 4. Iniciar a campanha (apenas se for "Enviar agora")
      if (sendImmediately) {
        console.log('🚀 Iniciando campanha...');
        await apiService.post(`/campaigns/${campaignId}/start`);
        
        toast({
          title: "🎉 Campanha criada e iniciada!",
          description: `A campanha "${campaignData.name}" foi iniciada e os disparos começarão em breve.`
        });
      } else {
        toast({
          title: "📅 Campanha agendada com sucesso!",
          description: `A campanha "${campaignData.name}" foi agendada para ${new Date(campaignData.scheduled_at).toLocaleString('pt-BR')}.`
        });
      }

      // Reset form
      setCampaignData({
        name: '',
        message: '',
        send_interval_seconds: 5,
        daily_limit: '',
        whatsapp_connection_id: '',
        scheduled_at: '',
        min_interval_seconds: 45,
        max_interval_seconds: 180
      });
      setContactsFile(null);
      setMediaFile(null);
      setHasMedia(false);
      setSendImmediately(true);

      onSuccess?.();
      onClose();

    } catch (error: any) {
      console.error('❌ Erro ao criar campanha:', error);
      toast({
        title: "Erro ao criar campanha",
        description: error.response?.data?.error || error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Nova Campanha de Disparo
          </DialogTitle>
          <DialogDescription>
            Crie e dispare uma nova campanha de mensagens WhatsApp
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações da Campanha</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sincronização ConnectChat */}
              <div className="pb-4 border-b border-gray-200">
                <Button
                  onClick={handleSyncConnectChat}
                  disabled={syncingConnectChat}
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                >
                  {syncingConnectChat ? (
                    <>
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-green-600 mr-2" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sincronizar Conexões ConnectChat
                    </>
                  )}
                </Button>
              </div>

              <div>
                <Label htmlFor="campaign-name">Nome da Campanha *</Label>
                <Input
                  id="campaign-name"
                  placeholder="Ex: Promoção Black Friday 2024"
                  value={campaignData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="message">Mensagem *</Label>
                <Textarea
                  id="message"
                  placeholder="Digite a mensagem que será enviada para os contatos..."
                  value={campaignData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="whatsapp-connection">Caixa de Entrada (WhatsApp) *</Label>
                {loadingConnections ? (
                  <div className="flex items-center gap-2 p-2 border rounded">
                    <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                    <span className="text-sm text-muted-foreground">Carregando conexões...</span>
                  </div>
                ) : whatsappConnections.length === 0 ? (
                  <div className="p-2 border border-red-200 rounded bg-red-50 text-red-600 text-sm">
                    ⚠️ Nenhuma conexão WhatsApp encontrada. Entre em contato com o suporte.
                  </div>
                ) : (
                  <select
                    id="whatsapp-connection"
                    value={campaignData.whatsapp_connection_id}
                    onChange={(e) => handleInputChange('whatsapp_connection_id', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecione uma conexão...</option>
                    {whatsappConnections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name} {connection.phone_number ? `(${connection.phone_number})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Escolha qual número WhatsApp será usado para enviar as mensagens
                </p>
              </div>

              {/* Agendamento */}
              <div>
                <Label>Quando enviar? *</Label>
                <div className="space-y-3 mt-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="send-now"
                      checked={sendImmediately}
                      onChange={() => setSendImmediately(true)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <Label htmlFor="send-now" className="text-sm font-normal cursor-pointer">
                      🚀 Enviar agora
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="schedule"
                      checked={!sendImmediately}
                      onChange={() => setSendImmediately(false)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <Label htmlFor="schedule" className="text-sm font-normal cursor-pointer">
                      📅 Agendar para depois
                    </Label>
                  </div>
                  
                  {!sendImmediately && (
                    <div className="mt-2">
                      <Label htmlFor="scheduled-date">Data e Hora do Agendamento *</Label>
                      <Input
                        id="scheduled-date"
                        type="datetime-local"
                        value={campaignData.scheduled_at}
                        onChange={(e) => handleInputChange('scheduled_at', e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        A campanha será executada automaticamente na data/hora especificada
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="daily-limit">Limite Diário (opcional)</Label>
                  <Input
                    id="daily-limit"
                    type="number"
                    placeholder="Ex: 1000"
                    value={campaignData.daily_limit}
                    onChange={(e) => handleInputChange('daily_limit', e.target.value)}
                  />
                </div>
              </div>

              {/* Intervalo Randomizado */}
              <div className="pt-4 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Intervalo Randomizado de Envios</h5>
                <p className="text-sm text-gray-600 mb-4">
                  Configure um intervalo aleatório entre envios para evitar detecção e banimento do WhatsApp
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min-interval">Tempo mínimo (segundos)</Label>
                    <Input
                      id="min-interval"
                      type="number"
                      min="1"
                      max="300"
                      value={campaignData.min_interval_seconds}
                      onChange={(e) => handleInputChange('min_interval_seconds', parseInt(e.target.value) || 45)}
                      placeholder="Ex: 45"
                    />
                    <p className="text-xs text-gray-500">Intervalo mínimo entre envios</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="max-interval">Tempo máximo (segundos)</Label>
                    <Input
                      id="max-interval"
                      type="number"
                      min="1"
                      max="600"
                      value={campaignData.max_interval_seconds}
                      onChange={(e) => handleInputChange('max_interval_seconds', parseInt(e.target.value) || 180)}
                      placeholder="Ex: 180"
                    />
                    <p className="text-xs text-gray-500">Intervalo máximo entre envios</p>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Como funciona:</strong> O sistema gerará um tempo aleatório entre {campaignData.min_interval_seconds} e {campaignData.max_interval_seconds} segundos 
                    para cada envio, tornando o padrão de envio menos previsível e reduzindo o risco de banimento.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload de Contatos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Arquivo de Contatos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="contacts-file">Arquivo (.xlsx, .xls, .csv) *</Label>
                <Input
                  id="contacts-file"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleContactsFileChange}
                  className="mt-1"
                />
                {contactsFile && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    {contactsFile.name} selecionado
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Configuração de Mídia */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="h-5 w-5" />
                Configuração de Mídia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="has-media"
                  checked={hasMedia}
                  onCheckedChange={setHasMedia}
                />
                <Label htmlFor="has-media">Enviar mídia junto com a mensagem</Label>
              </div>

              {hasMedia && (
                <div>
                  <Label htmlFor="media-file">
                    Arquivo de Mídia 
                    <span className="text-xs text-muted-foreground ml-1">
                      (usará endpoint /sendurl)
                    </span>
                  </Label>
                  <Input
                    id="media-file"
                    type="file"
                    accept="image/*,video/*,audio/*,.pdf"
                    onChange={handleMediaFileChange}
                    className="mt-1"
                  />
                  {mediaFile && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />
                      {mediaFile.name} selecionado
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Imagens, vídeos, áudios ou documentos PDF
                  </p>
                </div>
              )}

              {!hasMedia && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  Apenas texto será enviado (endpoint /sendtext)
                </div>
              )}
            </CardContent>
          </Card>

          {/* Botões de Ação */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Upload className="mr-2 h-4 w-4 animate-spin" />
                  {sendImmediately ? 'Criando e disparando...' : 'Criando e agendando...'}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {sendImmediately ? 'Criar e Disparar' : 'Criar e Agendar'}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientCampaignTrigger;
